
import Hero from './components/Hero';
import Technologies from './components/Technologies';
import Footer from './components/Footer';
import Navigation from './components/Navigation';
import AwardBadge from './components/AwardBadge';
import { trackEvent } from '../../lib/tracking';

export default function Home() {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <Hero />
      <Technologies />
      
      {/* Demo Section Title */}
      <section id="demo" className="pt-24 pb-4 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-[36px] md:text-[45px] lg:text-[54px] font-light text-[#F3220B]">Demo</h2>
        </div>
      </section>

      {/* Technology Sections */}
      <section className="pt-4 pb-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 space-y-[102px]">
          {/* Voice Anonymization */}
          <div>
            <div className="mb-3">
              <h3 className="text-base md:text-lg lg:text-2xl font-bold text-gray-900 mb-3 flex items-start gap-2">
                <span className="inline-block w-0 h-0 border-t-[0.4em] border-t-transparent border-b-[0.4em] border-b-transparent border-l-[0.5em] border-l-[#F3220B] flex-shrink-0 mt-1"></span>
                <span>Voice Anonymisation</span>
              </h3>
              <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed">
                Privacy-by-design voice communications through irreversible anonymisation of vocal biometric data.
              </p>
            </div>
            <div className="flex flex-col lg:flex-row gap-8 items-start">
              <div className="flex-1">
                <div className="relative aspect-video overflow-hidden shadow-2xl bg-gray-900">
                  <video
                    src="/video/VideoAnonymisation.mp4"
                    controls
                    preload="metadata"
                    className="w-full h-full object-contain"
                    onPlay={() => trackEvent('video_play', { video: 'voice_anonymisation' })}
                    onEnded={() => trackEvent('video_complete', { video: 'voice_anonymisation' })}
                  />
                </div>
                <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed mt-4">
                  Unlike simple voice modification or acoustic masking, real-time voice anonymization combines advanced DSP and AI to preserve intelligibility, linguistic content, and naturalness, while removing all vocal features usable for speaker recognition or verification. The process is strictly irreversible: re-identification of the original speaker is not possible, even when using state-of-the-art voice biometrics tools or advanced AI systems. This approach follows a privacy-by-design principle and fully complies with regulatory frameworks such as the GDPR and the AI Act, particularly for sensitive or critical use cases.
                </p>
              </div>
              <div className="flex flex-col gap-3">
                <a 
                  href="/pdf/CandyVoice_Voice_Anonymization.pdf" 
                  download
                  onClick={() => trackEvent('pdf_download', { pdf: 'voice_anonymization_en' })}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#2774CF] text-white rounded-lg hover:bg-[#1f63b5] transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-download-line"></i>
                  Technical datasheet (PDF)
                </a>
                <a 
                  href="/pdf/CandyVoice_Anonymisation_Vocale.pdf" 
                  download
                  onClick={() => trackEvent('pdf_download', { pdf: 'anonymisation_vocale_fr' })}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#2774CF] text-white rounded-lg hover:bg-[#1f63b5] transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-download-line"></i>
                  Fiche technique (PDF)
                </a>
              </div>
            </div>
          </div>

          {/* Deepfake Detection */}
          <div>
            <div className="mb-3">
              <h3 className="text-base md:text-lg lg:text-2xl font-bold text-gray-900 mb-3 flex items-start gap-2">
                <span className="inline-block w-0 h-0 border-t-[0.4em] border-t-transparent border-b-[0.4em] border-b-transparent border-l-[0.5em] border-l-[#F3220B] flex-shrink-0 mt-1"></span>
                <span>Voice Deepfake Detection</span>
              </h3>
              <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed">
                Trusted voice interactions enabled by real-time detection of synthetic and manipulated voices.
              </p>
            </div>
            <div className="flex flex-col lg:flex-row gap-8 items-start">
              <div className="flex-1">
                <div className="relative aspect-video overflow-hidden shadow-2xl bg-gray-900">
                  <video
                    src="/video/DeepFake.mp4"
                    controls
                    preload="metadata"
                    className="w-full h-full object-contain"
                    onPlay={() => trackEvent('video_play', { video: 'deepfake_detection' })}
                    onEnded={() => trackEvent('video_complete', { video: 'deepfake_detection' })}
                  />
                </div>
                <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed mt-4">
                  Our real-time deepfake detection software distinguishes in real time human voices from AI-generated speech. The analysis result is represented by two curves:&nbsp;- the red curve indicates the instantaneous probability of voice synthesis, expressed as a percentage (from 0 to 100%)- the blue curve represents a sliding time average over the last five seconds of the red curve. It smooths instantaneous variations and reduces the impact of isolated false positives or false negatives, providing a more robust and stable decision.When the curves remain in the lower part of the graph, the voice is considered to be authentically human. As they move toward the upper part of the graph, the voice is identified as synthetic.
                </p>
              </div>
              <div className="flex flex-col gap-3">
                <a 
                  href="/pdf/CandyVoice_Deepfake_Detection.pdf" 
                  download
                  onClick={() => trackEvent('pdf_download', { pdf: 'deepfake_detection_en' })}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#2774CF] text-white rounded-lg hover:bg-[#1f63b5] transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-download-line"></i>
                  Technical datasheet (PDF)
                </a>
                <a 
                  href="/pdf/CandyVoice_Detection_Deepfake.pdf" 
                  download
                  onClick={() => trackEvent('pdf_download', { pdf: 'detection_deepfake_fr' })}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#2774CF] text-white rounded-lg hover:bg-[#1f63b5] transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-download-line"></i>
                  Fiche technique (PDF)
                </a>
              </div>
            </div>
          </div>

          {/* Noise Filtering */}
          <div>
            <div className="mb-3">
              <h3 className="text-base md:text-lg lg:text-2xl font-bold text-gray-900 mb-3 flex items-start gap-2">
                <span className="inline-block w-0 h-0 border-t-[0.4em] border-t-transparent border-b-[0.4em] border-b-transparent border-l-[0.5em] border-l-[#F3220B] flex-shrink-0 mt-1"></span>
                <span>Noise Reduction & Speech Enhancement</span>
              </h3>
              <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed">
                Clear and natural voice communications through robust ambient noise reduction.
              </p>
            </div>
            <div className="flex flex-col lg:flex-row gap-8 items-start">
              <div className="flex-1">
                <div className="relative aspect-video overflow-hidden shadow-2xl bg-gray-900">
                  <video
                    src="/video/NoizzOff.mp4"
                    controls
                    preload="metadata"
                    className="w-full h-full object-contain"
                    onPlay={() => trackEvent('video_play', { video: 'noise_reduction' })}
                    onEnded={() => trackEvent('video_complete', { video: 'noise_reduction' })}
                  />
                </div>
                <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed mt-4">
                  The interface of our noise reduction filter demonstrator, NoizzOff, displays two real-time curves:- the red curve represents the input signal level before filtering,- while the blue curve shows the output signal level after filtering.The difference between the two curves directly reflects the amount of noise removed by NoizzOff.By switching the noise filter on and off, you can clearly hear NoizzOff's effectiveness in real time.
                </p>
              </div>
              <div className="flex flex-col gap-3">
                <a 
                  href="/military_12M_test/test_military_12M.html" 
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent('demo_click', { demo: 'military_12m_samples' })}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#2774CF] text-white rounded-lg hover:bg-[#1f63b5] transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-external-link-line"></i>
                  Various samples
                </a>
                <a 
                  href="/pdf/CandyVoice_Noise_Filter.pdf" 
                  download
                  onClick={() => trackEvent('pdf_download', { pdf: 'noise_filter_en' })}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#2774CF] text-white rounded-lg hover:bg-[#1f63b5] transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-download-line"></i>
                  Technical datasheet (PDF)
                </a>
                <a 
                  href="/pdf/CandyVoice_Filtre_de_Bruit.pdf" 
                  download
                  onClick={() => trackEvent('pdf_download', { pdf: 'filtre_de_bruit_fr' })}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#2774CF] text-white rounded-lg hover:bg-[#1f63b5] transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-download-line"></i>
                  Fiche technique (PDF)
                </a>
              </div>
            </div>
          </div>

          {/* Frame Recovery */}
          <div>
            <div className="mb-3">
              <h3 className="text-base md:text-lg lg:text-2xl font-bold text-gray-900 mb-3 flex items-start gap-2">
                <span className="inline-block w-0 h-0 border-t-[0.4em] border-t-transparent border-b-[0.4em] border-b-transparent border-l-[0.5em] border-l-[#F3220B] flex-shrink-0 mt-1"></span>
                <span>Packet Loss Concealment (PLC)</span>
              </h3>
              <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed">
                Seamless voice continuity ensured by real-time recovery of missing audio frames under degraded network conditions.
              </p>
            </div>
            <div className="flex flex-col lg:flex-row gap-8 items-start">
              <div className="flex-1">
                <div className="relative aspect-video overflow-hidden shadow-2xl bg-gray-900">
                  <video
                    src="/video/FrameRecover.mp4"
                    controls
                    preload="metadata"
                    className="w-full h-full object-contain"
                    onPlay={() => trackEvent('video_play', { video: 'packet_loss_concealment' })}
                    onEnded={() => trackEvent('video_complete', { video: 'packet_loss_concealment' })}
                  />
                </div>
                <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed mt-4">
                  Our Packet Loss Concealment technology restores missing audio frames in real time under degraded transmission conditions (interference, jamming, or unstable networks). By intelligently reconstructing missing segments of the speech signal, it can recover up to 50% of lost frames while preserving speech continuity, intelligibility, and naturalness. This reconstruction significantly improves perceived audio quality and maintains operational voice communications even under severe packet loss. On the graph, the red curve illustrates the degraded input signal with frame loss, while the blue curve represents the reconstructed output signal after processing.
                </p>
              </div>
              <div className="flex flex-col gap-3">
                <a 
                  href="/pdf/CandyVoice_Frame_Reconstruction.pdf" 
                  download
                  onClick={() => trackEvent('pdf_download', { pdf: 'frame_reconstruction_en' })}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#2774CF] text-white rounded-lg hover:bg-[#1f63b5] transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-download-line"></i>
                  Technical datasheet (PDF)
                </a>
                <a 
                  href="/pdf/CandyVoice_Recupération_des_Trames.pdf" 
                  download
                  onClick={() => trackEvent('pdf_download', { pdf: 'recuperation_trames_fr' })}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#2774CF] text-white rounded-lg hover:bg-[#1f63b5] transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-download-line"></i>
                  Fiche technique (PDF)
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

     {/* Recognition & Strategic Awards Section */}
      <section id="recognition" className="py-24 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-[36px] md:text-[45px] lg:text-[54px] font-light text-[#F3220B] mb-12">
            Recognition & Strategic Awards
          </h2>

          <div className="space-y-14 md:space-y-16">

            {/* ICCA 2026 Finalist */}
            <div className="flex flex-col md:flex-row items-center md:items-center gap-6 md:gap-12">
              <div className="w-[201px] md:w-[231px] flex-shrink-0 flex items-center justify-center">
                <img
                  src="/images/recognition/ICCA_2026_Winner.png"
                  alt="ICCA 2026 Finalist"
                  className="max-w-full h-auto"
                />
              </div>
              <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed text-left">
                CandyVoice is honoured to have won the <strong>ICCA 2026 (International Critical Communications Awards)</strong> in the category <strong>"Best Use of Advanced Technology"</strong> for its <strong>Mission-Critical AI Voice Stack</strong>. Selected by a jury of 60 international experts, this award recognises CandyVoice's innovative suite of AI-powered voice technologies for mission-critical communications, to improve the security, resilience and intelligibility of tactical radio communications. Supported by the Critical Communications Association (TCCA), the ICCA is one of the most prestigious international awards in the critical communications industry.
                {" "}
                <a
                  href="https://www.critical-communications-world.com/2026-icca-winners"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent('partner_click', { partner: 'ICCA' })}
                  className="text-blue-600 hover:underline"
                >
                  (Read the official ICCA 2026 winners announcement)
                </a>
              </p>
            </div>

            {/* BRIDGE Programme */}
            <div className="flex flex-col md:flex-row items-center md:items-center gap-6 md:gap-12">
              <div className="w-[201px] md:w-[231px] flex-shrink-0 flex items-center justify-center">
                <img
                  src="/images/recognition/bridge.png"
                  alt="BRIDGE Programme — Ministère des Armées et CAPR"
                  className="max-w-full h-auto"
                />
              </div>
              <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed text-left">
                Winner of the <strong>2024 edition of the BRIDGE programme</strong>, an innovation initiative launched by the Commandement des Actions dans la Profondeur et du Renseignement (CAPR) of the French Army, based in Strasbourg. This programme fosters synergies between armed forces, technology companies and academic research to support the operational capabilities of tomorrow.
              </p>
            </div>

            {/* Bouygues Innovation */}
            <div className="flex flex-col md:flex-row items-center md:items-center gap-6 md:gap-12">
              <div className="w-[201px] md:w-[231px] flex-shrink-0 flex items-center justify-center">
                <img
                  src="/images/recognition/bouygues-telecom.png"
                  alt="Bouygues Telecom"
                  className="max-w-[146px] md:max-w-[166px] h-auto"
                />
              </div>
              <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed text-left">
                <strong>Innovation for a Better Customer Experience Award</strong>, presented by Bouygues Innovation in Paris in 2022, recognising the outstanding performance of CandyVoice's advanced noise filtering technology and its contribution to clearer, more intelligible voice communications.
              </p>
            </div>

          </div>
        </div>
      </section>

     {/* Partners Section */}
      <section id="partners" className="py-24 md:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-[36px] md:text-[45px] lg:text-[54px] font-light text-[#F3220B] mb-12">
            Partners
          </h2>

          {/* Première ligne : 5 logos répartis */}
          <div className="flex flex-wrap justify-between items-center gap-y-8 mb-10">

            <a href="https://www.startup-semia.com/" target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('partner_click', { partner: 'semia' })} className="block group flex-1 flex justify-center min-w-[120px]">
              <img src="/images/partners/semia.jpg" alt="SEMIA" className="h-10 md:h-12 w-auto object-contain transition-transform group-hover:scale-105" />
            </a>

            <a href="https://lafrenchtechest.fr/" target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('partner_click', { partner: 'french_tech_est' })} className="block group flex-1 flex justify-center min-w-[120px]">
              <img src="/images/partners/french-tech-est.png" alt="La French Tech Est" className="h-32 md:h-40 w-auto object-contain transition-transform group-hover:scale-105" />
            </a>

            <a href="https://www.cybersecurite.grandest.fr/" target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('partner_click', { partner: 'hub_grand_est_cyber' })} className="block group flex-1 flex justify-center min-w-[120px]">
              <img src="/images/partners/hub-grand-est-cyber.png" alt="Le Hub Grand Est Cybersécurité" className="h-20 md:h-24 w-auto object-contain transition-transform group-hover:scale-105" />
            </a>

            <a href="https://www.levoicelab.org/" target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('partner_click', { partner: 'voice_lab' })} className="block group flex-1 flex justify-center min-w-[120px]">
              <img src="/images/partners/voice-lab.png" alt="Le Voice Lab" className="h-28 md:h-32 w-auto object-contain transition-transform group-hover:scale-105" />
            </a>

            <a href="https://www.bpifrance.fr/" target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('partner_click', { partner: 'bpi_france' })} className="block group flex-1 flex justify-center min-w-[120px]">
              <img src="/images/partners/bpi-france.jpg" alt="BPI France" className="h-12 md:h-[60px] w-auto object-contain transition-transform group-hover:scale-105" />
            </a>

          </div>

          {/* Deuxième ligne : 4 logos répartis sur la même largeur que ci-dessus */}
          <div className="flex flex-wrap justify-between items-center gap-y-8">

            <a href="https://www.grandest.fr/" target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('partner_click', { partner: 'region_grand_est' })} className="block group flex-1 flex justify-center min-w-[120px]">
              <img src="/images/partners/region-grand-est.png" alt="Région Grand Est" className="h-[47px] md:h-[57px] w-auto object-contain transition-transform group-hover:scale-105" />
            </a>

            <a href="https://www.scaleway.com/fr/" target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('partner_click', { partner: 'scaleway' })} className="block group flex-1 flex justify-center min-w-[120px]">
              <img src="/images/partners/scaleway.png" alt="Scaleway" className="h-9 md:h-[47px] w-auto object-contain transition-transform group-hover:scale-105" />
            </a>

            <a href="https://www.alsace-eurometropole.cci.fr/" target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('partner_click', { partner: 'cci_alsace_eurometropole' })} className="block group flex-1 flex justify-center min-w-[120px]">
              <img src="/images/partners/cci-alsace.png" alt="CCI Alsace Eurométropole" className="h-[42px] md:h-[52px] w-auto object-contain transition-transform group-hover:scale-105" />
            </a>

            <a href="https://www.hub-franceia.fr/" target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('partner_click', { partner: 'hub_france_ia' })} className="block group flex-1 flex justify-center min-w-[120px]">
              <img src="/images/partners/hub-france-ia.jpg" alt="Hub France IA" className="h-[62px] md:h-[73px] w-auto object-contain transition-transform group-hover:scale-105" />
            </a>

          </div>
        </div>
      </section>

     {/* Contact Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-[36px] md:text-[45px] lg:text-[54px] font-light text-[#F3220B]">Contact</h2>
          <p className="text-lg text-gray-600 mb-8">
            For any information request, please use our secure contact form.
          </p>
          <div className="w-full overflow-hidden rounded-xl border border-gray-200">
            <iframe
              src="/contact/"
              title="CandyVoice Contact Form"
              className="w-full"
              style={{ height: "650px" }}
            />
          </div>
        </div>
      </section>

      <Footer />
      <AwardBadge />
    </div>
  );
}
